import type { CardLanguage, PortfolioEntry, PriceQuote } from "../types";
import type { Settings } from "../settings";

// Accounts and saved data (specs/11). Platform-free: the Supabase versions live in
// src/platform/web/, and tests use fakes.

export interface Account {
  id: string;
  email: string;
  method: "email" | "google";
  name?: string;
  avatarUrl?: string;
}

/** What an invite or password-reset link opened the app for (11 FR-57, FR-61). */
export type LinkEvent = "invite" | "password-reset";

export type AccountErrorKind =
  | "invalid-credentials"
  | "rate-limit"
  | "weak-password"
  | "same-password"
  | "email-not-confirmed"
  | "network"
  // From a link or a Google sign-in that came back with an error (11 edge cases).
  | "link-expired"
  | "google-not-invited"
  | "google-cancelled"
  | "google-failed"
  | "unknown";

export class AccountError extends Error {
  constructor(
    readonly kind: AccountErrorKind,
    message: string = kind,
  ) {
    super(message);
    this.name = "AccountError";
  }
}

export const accountErrorKind = (error: unknown): AccountErrorKind =>
  error instanceof AccountError ? error.kind : "unknown";

/** Every method rejects with an AccountError. */
export interface AccountService {
  current(): Promise<Account | null>;
  /**
   * Asks the server whether the signed-in account still exists and its session is still valid,
   * and signs out on this device if not (account deleted in the dashboard, signed out
   * elsewhere). Fails quietly when the server can't be reached.
   */
  verify(): Promise<void>;
  onChange(cb: (account: Account | null, event?: LinkEvent) => void): () => void;
  /** The error an invite, reset or Google link brought back to the app on this page load. */
  linkError(): AccountErrorKind | null;
  signIn(email: string, password: string): Promise<void>;
  /** Leaves the page; the result arrives through onChange (or linkError) on return. */
  signInWithGoogle(): Promise<void>;
  signOut(everywhere?: boolean): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  /** Used by /welcome and /reset-password. */
  setPassword(password: string): Promise<void>;
  deleteAccount(): Promise<void>;
}

/**
 * What one portfolio change did to the saved entries. The reducer's actions alone can't say
 * that (an `add` may merge into an existing entry), so the app saves the difference between
 * the entries before and after the action (`diffEntries`).
 */
export interface PortfolioChange {
  /** Entries that are new or changed. */
  upsert: PortfolioEntry[];
  /** Ids of entries that are gone. */
  remove: string[];
}

export interface PortfolioStore {
  load(): Promise<PortfolioEntry[]>;
  save(change: PortfolioChange): Promise<void>;
}

/** A card in the shared price cache (SPECS D20). */
export interface CardRef {
  language: CardLanguage;
  cardId: string;
}

export interface CachedPrices extends CardRef {
  prices: PriceQuote[];
  /** When the prices were fetched (ISO), set by the database. */
  fetchedAt: string;
}

/** The shared price cache (04 FR-22a – FR-22e). */
export interface PriceStore {
  /** Saved prices for these cards. Cards without any (or with invalid rows) are left out. */
  load(cards: CardRef[]): Promise<CachedPrices[]>;
  /** Only cards in the account's own portfolio can be saved (FR-22d). */
  save(prices: (CardRef & { prices: PriceQuote[] })[]): Promise<void>;
}

export interface SettingsStore {
  /** null when nothing has been saved yet (11 FR-71). */
  load(): Promise<Settings | null>;
  save(settings: Settings): Promise<void>;
}

export interface AccountServices {
  account: AccountService;
  /** The stores for a signed-in account's data. */
  stores(account: Account): {
    portfolio: PortfolioStore;
    settings: SettingsStore;
    prices: PriceStore;
  };
}
